import { zDriverLicenseCategory } from '@/general/src/insuranceDoc/driverLicense/shared.js'
import { getInsuranceDocMap, zClientInsuranceDocUploads } from '@/general/src/insuranceDoc/shared.js'
import { z } from 'zod'

const ptsCsvSrc = `Название поля в программе,alias,Название поля на фотографии в документе,Подсказка для ИИ,Валидация,Пример заполнения
Серия ПТС,series,,"Серия и номер находятся в самом вверху и выделено красным цветом, серия всегда состоит из 4 символов, цифр и букв и находится перед номером",обязательно,25 МА
Номер ПТС,number,,"Серия и номер находятся в самом вверху и выделено красным цветом, номер всегда состоит из 6 цифр и находится после серии","обязательно, DDDDDD ",19989
Идентификационный номер (VIN),vin,1. Идентификационный номер (VIN),"17 символов, цифры и латинские буквы, может отсутствовать",опционально,ОТСУТСТВУЕТ
Марка ТС,brand,"2. Марка, модель ТС","марка автомобиля, часто пишется вместе с моделью автомобиля, но находится перед ней",обязательно,TOYOTA
Модель ТС,model,"2. Марка, модель ТС","модель автомобиля, часто пишется вместе с маркой автомобиля, но находится после нее",обязательно,CARINA
Тип ТС,type,3. Наименование (тип ТС),"Тип транспортного стредства, бывает разный, например ""Седан"" , ""Внедорожник"" , ""Грузовой""",обязательно,СЕДАН
Категория ТС,category,"4. Категория ТС (А, В, С, D, прицеп)","Аналогично категории прав. Буква или буква с цифрой, бывают: A, B, C, D, E, A1, B1, C1, D1, BE, CE, DE, C1E, D1E, M, Tm, Tb.",обязательно,B
Год изготовления ТС,year,5. Год изготовления ТС,год изготовления или выпуска транспортного средства,обязательно,1993 (ТРИН)
"Модель, № двигателя",engineNumber,"6. Модель, № двигателя","цифры и латинские буквы, может отсутствовать",опционально,4S1008187
Шасси (рама) №,chassisNumber,7. Шасси (рама) №,"цифры и латинские буквы, может отсутствовать",опционально,ОТСУТСТВУЕТ
"Кузов (кабина, прицеп) №",frameNumber,"8. Кузов (кабина, прицеп) №","цифры и латинские буквы, может отсутствовать, а может совпадать с VIN",опционально,ST1906036498
Цвет,color,"9. Цвет кузова  (кабины, прицепа)","цвет транспортного средства, например ""зеленый""",обязательно,БЕЛЫЙ
"Мощность двигателя, л.с.",powerHp,"10. Мощность двигателя, л.с. (кВт)","В документе может называться просто мощность, но если есть приписка лс, то это оно. Если указано только лс или только кВТ. Высчитай недостающее поле сам исходя из того что 1 лс = 0,7355 кВТ",обязательно,125
"Мощность двигателя, кВт",powerKw,"10. Мощность двигателя, л.с. (кВт)","В документе может называться просто мощность, но если есть приписка кВТ, то это оно",обязательно,92
"Рабочий объем двигателя, куб. см",engineVolume,"11. Рабочий объем двигателя, куб. см",Цифры,обязательно,1838
Тип двигателя,engineType,12. Тип двигателя,"Тип двигателя, может быть разный, например ""Бензин"" или ""Дизель""",обязательно,БЕНЗИН
Дата выдачи ПТС,issuedDate,13. Дата выдачи ПТС,дата выдачи ПТС,обязательно,01.01.2020`

export const zPts = z.object({
  series: z.string().min(1),
  number: z.string().min(1),
  vin: z.string().min(1),
  brand: z.string().min(1),
  model: z.string().min(1),
  type: z.string().min(1),
  category: zDriverLicenseCategory,
  year: z.string().min(1),
  engineNumber: z.string().optional(),
  chassisNumber: z.string().optional(),
  frameNumber: z.string().optional(),
  color: z.string().min(1),
  powerHp: z.string().min(1),
  powerKw: z.string().min(1),
  engineVolume: z.string().min(1),
  engineType: z.string().min(1),
  issuedDate: z.string().min(1),
})
export type PtsWithoutUploads = z.output<typeof zPts>
export const zPtsWithUploads = z.object({
  insuranceDocsUploads: zClientInsuranceDocUploads,
  ...zPts.shape,
})

export const ptsFieldsMap = getInsuranceDocMap({
  csv: ptsCsvSrc,
  zSchema: zPtsWithUploads,
  extra: {
    insuranceDocsUploads: { label: 'Фотография ПТС (лицевая сторона)' },
  },
})
export const getPtsFieldTitle = (field: keyof typeof ptsFieldsMap) => ptsFieldsMap[field].label
export const getPtsFieldHint = (field: keyof typeof ptsFieldsMap) => ptsFieldsMap[field].hint

export const ptsInitialValues: z.infer<typeof zPtsWithUploads> = {
  insuranceDocsUploads: [],
  series: '',
  number: '',
  vin: '',
  brand: '',
  model: '',
  type: '',
  category: '' as never,
  year: '',
  engineNumber: '',
  chassisNumber: '',
  frameNumber: '',
  color: '',
  powerHp: '',
  powerKw: '',
  engineVolume: '',
  engineType: '',
  issuedDate: '',
}

export const ptsExampleValues: z.infer<typeof zPtsWithUploads> = {
  insuranceDocsUploads: [],
  series: '25МА',
  number: '013912',
  vin: '12345678901234567',
  brand: 'Toyota',
  model: 'Camry',
  type: 'Легковой автомобиль',
  category: 'A',
  year: '2020',
  engineNumber: '4S1008187',
  chassisNumber: '12345678901234567',
  frameNumber: 'ST1906036498',
  color: 'Красный',
  powerHp: '200',
  powerKw: '150',
  engineVolume: '1.5',
  engineType: 'Бензиновый',
  issuedDate: '01.01.2020',
}
